<script setup lang="ts">
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import LoginForm from '@/components/Auth/LoginForm.vue'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({ meta: { ...PageMeta.Auth.login.meta, pageTitle: PageMeta.Auth.login.title } })

const display = useDisplay()
const isDesktopLogin = computed(() => display.lgAndUp.value)
</script>

<template>
  <main class="login-page">
    <VRow
      v-if="isDesktopLogin"
      no-gutters
      class="desktop-login"
    >
      <VCol
        cols="12"
        lg="7"
        class="desktop-login__visual"
      >
        <div class="desktop-login__grid" />

        <div class="desktop-login__content">
          <div class="desktop-login__brand">
            <VNodeRenderer
              :nodes="themeConfig.app.logo"
              class="desktop-login__brand-mark"
            />
            <span class="desktop-login__brand-name">{{ themeConfig.app.title }}</span>
          </div>

          <div class="desktop-login__message">
            <p class="desktop-login__kicker">Vortex oversight</p>
            <h1 class="desktop-login__title">All knowing.<br />Ever powerful.</h1>
            <p class="desktop-login__copy">
              Move from insight to action in a workspace built for clear, connected operations.
            </p>
          </div>

          <div class="desktop-login__status">
            <span class="desktop-login__status-indicator" />
            <span>Secure workspace access</span>
          </div>
        </div>

        <div
          class="desktop-login__orbit"
          aria-hidden="true"
        >
          <span class="desktop-login__ring desktop-login__ring--outer" />
          <span class="desktop-login__ring desktop-login__ring--middle" />
          <span class="desktop-login__ring desktop-login__ring--inner" />
          <span class="desktop-login__core" />
        </div>
      </VCol>

      <VCol
        cols="12"
        lg="5"
        class="desktop-login__panel"
      >
        <LoginForm class="desktop-login__form" />
      </VCol>
    </VRow>

    <section
      v-else
      class="mobile-login"
    >
      <div class="mobile-login__grid" />
      <div class="mobile-login__glow" />

      <div
        class="mobile-login__orbit"
        aria-hidden="true"
      >
        <span class="mobile-login__ring mobile-login__ring--outer" />
        <span class="mobile-login__ring mobile-login__ring--inner" />
        <span class="mobile-login__core" />
      </div>

      <div class="mobile-login__surface">
        <LoginForm show-brand />
      </div>

      <div class="mobile-login__status">
        <span class="mobile-login__status-indicator" />
        <span>Secure workspace access</span>
      </div>
    </section>
  </main>
</template>

<style scoped lang="scss">
.login-page,
.desktop-login,
.mobile-login {
  min-block-size: 100dvh;
}

.login-page {
  background: rgb(var(--v-theme-background));
  font-family: var(--app-font-ui);
}

.desktop-login__visual {
  position: relative;
  display: flex;
  overflow: hidden;
  align-items: stretch;
  background:
    radial-gradient(circle at 76% 32%, rgba(var(--v-theme-primary), 0.2), transparent 32%),
    linear-gradient(145deg, rgb(var(--v-theme-surface-subtle)), rgb(var(--v-theme-background)));
}

.desktop-login__grid,
.mobile-login__grid {
  position: absolute;
  background-image:
    linear-gradient(rgba(var(--v-theme-on-background), 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(var(--v-theme-on-background), 0.045) 1px, transparent 1px);
  inset: 0;
  mask-image: linear-gradient(to bottom, transparent, black 18%, black 82%, transparent);
}

.desktop-login__grid {
  background-size: 48px 48px;
}

.desktop-login__content {
  position: relative;
  z-index: 2;
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: clamp(2rem, 4vw, 4.5rem);
}

.desktop-login__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
}

.desktop-login__brand-mark {
  display: grid;
  inline-size: 2.25rem;
  place-items: center;
}

.desktop-login__brand-name {
  color: rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity));
  font-family: var(--app-font-ui);
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.025em;
}

.desktop-login__message {
  max-inline-size: 38rem;
  margin-block: auto;
}

.desktop-login__kicker {
  margin-block-end: 1rem;
  color: rgb(var(--v-theme-primary));
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.desktop-login__title {
  margin: 0;
  color: rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity));
  font-family: var(--app-font-ui);
  font-size: clamp(3.5rem, 5.5vw, 6.25rem);
  font-weight: 700;
  letter-spacing: -0.065em;
  line-height: 0.92;
}

.desktop-login__copy {
  max-inline-size: 32rem;
  margin-block: 2rem 0;
  color: rgba(var(--v-theme-on-background), var(--v-medium-emphasis-opacity));
  font-size: 1.05rem;
  line-height: 1.7;
}

.desktop-login__status,
.mobile-login__status {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: rgba(var(--v-theme-on-background), var(--v-medium-emphasis-opacity));
  font-size: 0.8rem;
  letter-spacing: 0.04em;
}

.desktop-login__status-indicator,
.mobile-login__status-indicator {
  inline-size: 0.5rem;
  aspect-ratio: 1;
  border-radius: 50%;
  background: rgb(var(--v-theme-success));
  box-shadow: 0 0 0 0.25rem rgba(var(--v-theme-success), 0.12);
}

.desktop-login__orbit {
  position: absolute;
  inline-size: min(43vw, 42rem);
  aspect-ratio: 1;
  inset-block-start: 50%;
  inset-inline-end: -27%;
  pointer-events: none;
  transform: translateY(-50%);
}

.desktop-login__ring,
.desktop-login__core,
.mobile-login__ring,
.mobile-login__core {
  position: absolute;
  border-radius: 50%;
  inset: 50%;
  transform: translate(-50%, -50%);
}

.desktop-login__ring {
  border: 1px solid rgba(var(--v-theme-primary), 0.22);
}

.desktop-login__ring--outer {
  inline-size: 100%;
  block-size: 100%;
}

.desktop-login__ring--middle {
  inline-size: 70%;
  block-size: 70%;
  border-color: rgba(var(--v-theme-primary), 0.36);
}

.desktop-login__ring--inner {
  inline-size: 40%;
  block-size: 40%;
  border-color: rgba(var(--v-theme-primary), 0.52);
}

.desktop-login__core {
  inline-size: 12%;
  block-size: 12%;
  background: rgb(var(--v-theme-primary));
  box-shadow:
    0 0 4rem rgba(var(--v-theme-primary), 0.48),
    0 0 8rem rgba(var(--v-theme-primary), 0.28);
}

.desktop-login__panel {
  display: flex;
  align-items: center;
  justify-content: center;
  min-block-size: 100dvh;
  padding: clamp(2rem, 5vw, 5.5rem);
  background: rgb(var(--v-theme-surface));
}

.desktop-login__form {
  animation: login-form-enter 520ms ease-out both;
}

.mobile-login {
  position: relative;
  display: grid;
  overflow: hidden;
  align-items: center;
  padding: clamp(1.25rem, 5vw, 2.5rem);
  background:
    radial-gradient(circle at 82% 12%, rgba(var(--v-theme-primary), 0.25), transparent 28%),
    linear-gradient(160deg, rgb(var(--v-theme-surface-subtle)), rgb(var(--v-theme-background)) 62%);
}

.mobile-login__grid {
  background-size: 36px 36px;
  opacity: 0.7;
}

.mobile-login__glow {
  position: absolute;
  inline-size: 18rem;
  aspect-ratio: 1;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.12);
  filter: blur(5rem);
  inset-block-end: -8rem;
  inset-inline-start: -8rem;
}

.mobile-login__orbit {
  position: absolute;
  inline-size: min(82vw, 26rem);
  aspect-ratio: 1;
  inset-block-start: -18%;
  inset-inline-end: -34%;
  pointer-events: none;
}

.mobile-login__ring {
  border: 1px solid rgba(var(--v-theme-primary), 0.25);
}

.mobile-login__ring--outer {
  inline-size: 100%;
  block-size: 100%;
}

.mobile-login__ring--inner {
  inline-size: 56%;
  block-size: 56%;
  border-color: rgba(var(--v-theme-primary), 0.44);
}

.mobile-login__core {
  inline-size: 12%;
  block-size: 12%;
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 0 4rem rgba(var(--v-theme-primary), 0.55);
}

.mobile-login__surface {
  position: relative;
  z-index: 2;
  inline-size: min(100%, 34rem);
  margin-inline: auto;
  padding: clamp(1.5rem, 6vw, 2.75rem);
  border: 0;
  border-radius: 1.75rem;
  background: rgba(var(--v-theme-surface), 0.9);
  box-shadow: 0 2rem 6rem rgba(var(--v-shadow-key-umbra-color), 0.28);
  backdrop-filter: blur(1.5rem);
}

.mobile-login__status {
  position: absolute;
  z-index: 2;
  inset-block-end: 1.25rem;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
}

@media (max-height: 760px) {
  .mobile-login {
    align-items: start;
    overflow-y: auto;
    padding-block: 1rem 4.5rem;
  }

  .mobile-login__surface {
    padding: 1.5rem;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .desktop-login__core,
  .mobile-login__core {
    animation: login-pulse 4s ease-in-out infinite;
  }

  .mobile-login__surface {
    animation: login-form-enter 520ms ease-out both;
  }
}

@keyframes login-pulse {
  50% {
    transform: translate(-50%, -50%) scale(1.08);
  }
}

@keyframes login-form-enter {
  from {
    opacity: 0;
    transform: translateY(0.75rem);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
